import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Center, VStack } from "../../../../styled-system/jsx";
import { Modal } from "../../../components/core/basic/Modal";
import { Button } from "../../../components/core/basic/Button";
import { TextField } from "../../../components/fields/base/TextField";
import { useWalmartAdvertisingSetup } from "./api/useWalmartAdvertisingSetup";
import { useWalmartOnboardingStatus } from "./useWalmartOnboardingStatus";
import { WalmartPartnerAccessInstructions } from "./WalmartPartnerAccessInstructions";
import { useAdvertiserVerificationError, type AdvertiserVerificationError } from "./useAdvertiserVerificationError";

interface WalmartAdvertisingSetupProps {
  open: boolean;
  onClose: () => void;
  reauth?: boolean;
}

export const WalmartAdvertisingSetup = ({ open, onClose, reauth }: WalmartAdvertisingSetupProps) => {
  const [draftAdvertiserId, setDraftAdvertiserId] = useState<string | null>(null);
  const [failure, setFailure] = useState<AdvertiserVerificationError | null>(null);
  const { t } = useTranslation(["onboarding", "common"]);

  const describeError = useAdvertiserVerificationError();
  const mutation = useWalmartAdvertisingSetup();

  // Show what is connected today rather than a blank field, which reads as "nothing is set".
  const { data: onboardingStatus } = useWalmartOnboardingStatus();
  const advertiserId = draftAdvertiserId ?? onboardingStatus?.advertiser_id ?? "";

  const submit = (force: boolean) =>
    mutation.mutate(
      { advertiserId, reauth, force },
      {
        onSuccess: () => {
          setFailure(null);
          onClose();
        },
        onError: (error) => setFailure(describeError(error)),
      },
    );

  return (
    <Modal isOpen={open} onOpenChange={(isOpen) => !isOpen && onClose()} css={{ width: "lg" }}>
      <VStack gap="large" paddingX="large" paddingBottom="large" alignItems="stretch">
        <WalmartPartnerAccessInstructions />

        <TextField
          type="text"
          name="advertiserId"
          label={t(($) => $.onboarding["Advertiser ID"])}
          value={advertiserId}
          onChange={(value) => {
            setDraftAdvertiserId(value);
            setFailure(null);
          }}
          placeholder={t(($) => $.onboarding["Enter Walmart Advertiser ID"])}
          description={t(
            ($) =>
              $.onboarding[
                "On your Ad Center home screen, choose a Partner, Organization and Account — the Advertiser ID is the number under the account name."
              ],
          )}
          errorMessage={failure?.message}
        />
        <Center>
          <Button
            variant="primary"
            colorScheme="fuchsia"
            isDisabled={!advertiserId}
            isPending={mutation.isPending}
            onPress={() => submit(false)}
          >
            {t(($) => $.onboarding["Verify & Connect"])}
          </Button>
        </Center>

        {failure?.retryable && (
          <Center>
            <Button variant="link" isPending={mutation.isPending} onPress={() => submit(true)}>
              {t(($) => $.onboarding["I have already added Trellis — save this ID and continue"])}
            </Button>
          </Center>
        )}
      </VStack>
    </Modal>
  );
};
